import { UserField } from '@dadohr/shared/domainTypes/experience';
import { isCustomFieldKey } from '@dadohr/shared/domainTypes/org';
import { Rule } from '@dadohr/shared/domainTypes/rules';
import { Button, Typography } from '@material-ui/core';
import { Download, Edit, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Redirect } from 'react-router';
import { ButtonWithPromise } from '../../../../components/ButtonWithPromise';
import { ColumnSelector } from '../../../../components/ColumnSelector';
import { Loader } from '../../../../components/Loader';
import { NoAccess } from '../../../../components/NoAccess';
import { SaveViewModal } from '../../../../components/SaveViewModal';
import { TableSearch } from '../../../../components/Table';
import { UserFilterDialog } from '../../../../components/UserFilterDialog';
import { UserTable } from '../../../../components/UserTable';
import {
  DETAILED_NAME_COLUMNS,
  useUserTableColumnsAndSorters
} from '../../../../components/UserTable/columns';
import { useExportUsersCsv } from '../../../../components/UserTable/useExportUsersCsv';
import { useDialogState } from '../../../../hooks/useDialogState';
import { useRouter } from '../../../../hooks/useRouter';
import { useUnsafeTranslation } from '../../../../i18next';
import { FlexContainer } from '../../../../layout/Flex';
import { PageBody } from '../../../../layout/PageBody';
import {
  PageToolbar,
  PageToolbarActions,
  PageToolbarTitle
} from '../../../../layout/PageToolbar';
import { useRoutes, useStringQueryParam } from '../../../../routes';
import { useCurrentUser } from '../../../../services/currentUser';
import { isFreeTierOrg } from '../../../../services/organizationTier';
import { hasPermissionToAccessPage } from '../../../../services/permissions';
import { createUserSavedView } from '../../../../services/savedView';
import { now } from '../../../../services/time';
import {
  matchUser,
  matchUserBySearchTerm,
  toSearchTermRegexp,
  useUsersByOrgId
} from '../../../../services/user';
import { isEmptyRule } from '../../../Experiences/components/RuleBuilder';
import { useUsersSideNav } from '../sideNav';

const ContentOrgUsersOverview = () => {
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();
  const sideNav = useUsersSideNav();
  const [users, loading] = useUsersByOrgId(org.id);
  const { ROUTES } = useRoutes();
  const { history } = useRouter();
  const [q, setQ] = useStringQueryParam('q', '');
  const filterDialog = useDialogState();
  const saveViewModal = useDialogState();
  const [filters, setFilters] = useState<Rule<UserField> | null>(null);
  const hasFiltersApplied = filters && !isEmptyRule(filters);
  const { columns: allColumns, sorters } = useUserTableColumnsAndSorters(
    org.customUserFields
  );
  const columns = useMemo(
    () => allColumns.filter((c) => !DETAILED_NAME_COLUMNS.includes(c.key)),
    [allColumns]
  );
  const defaultColumns = useMemo(
    () =>
      columns.filter(
        (c) =>
          !isCustomFieldKey(c.key) &&
          c.key !== 'alternateId' &&
          c.key !== 'phoneNumber' &&
          c.key !== 'address' &&
          c.key !== 'terminationDate' &&
          c.key !== 'dateOfBirth' &&
          c.key !== 'nextBirthday'
      ),
    [columns]
  );
  const [selectedColumns, setSelectedColumns] = useState(defaultColumns);
  const [sortKey, setSortKey] = useState('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const columnsChanged = selectedColumns.length !== defaultColumns.length ||
    selectedColumns.some((c, i) => c.key !== defaultColumns[i].key);
  const isCustomized = hasFiltersApplied || columnsChanged;

  const selectedUsers = useMemo(() => {
    if (!users) {
      return null;
    }
    let filteredUsers = users;

    if (q) {
      const regexp = toSearchTermRegexp(q);
      filteredUsers = filteredUsers.filter(u => matchUserBySearchTerm(regexp, u));
    }

    if (filters && !isEmptyRule(filters)) {
      filteredUsers = filteredUsers.filter(u => matchUser(u, filters, now()));
    }

    return filteredUsers;
  }, [q, users, filters]);

  const { exportCsv, canExport } = useExportUsersCsv({
    users,
    selectedUsers,
    selectedColumns,
    customUserFields: org.customUserFields,
    dateFormat: org.dateFormats.MMDDYYYY
  });

  const handleSaveView = async (name: string) => {
    const result = await createUserSavedView({
      orgId: org.id,
      name,
      filters,
      columns: selectedColumns.map((c) => c.key),
      sortKey,
      sortDirection
    });
    history.push(ROUTES.users.views.detail.url(result.id), { newViewId: result.id });
  };

  return (
    <PageBody noTopPadding sideNav={sideNav}>
      <PageToolbar sticky>
        <PageToolbarTitle>
          <Typography component="h1" variant="h1">
            {t('pageTitle.orgUsersOverview')}
          </Typography>
        </PageToolbarTitle>
        <PageToolbarActions>
          {isCustomized && (
            <Button
              color="primary"
              size="small"
              variant="text"
              startIcon={<Plus size={12} />}
              onClick={() => saveViewModal.openDialog()}
            >
              {t('Save this view')}
            </Button>
          )}
          <Button
            color="primary"
            size={undefined}
            variant={hasFiltersApplied ? 'contained' : 'outlined'}
            startIcon={undefined}
            onClick={() => filterDialog.openDialog()}
          >
            {hasFiltersApplied ? t('Edit filters') : t('Add filters')}
          </Button>
          <ColumnSelector
            value={selectedColumns}
            onChange={setSelectedColumns}
            columns={columns}
            requiredColumns={new Set(['name'])}
            withAllOption
            withSearch
            label={t('Edit columns')}
          />
          {canExport && (
            <ButtonWithPromise
              startIcon={<Download size={16} />}
              variant="outlined"
              pending={`${t('Exporting')}...`}
              onClick={exportCsv}
            >
              {t('Export')}
            </ButtonWithPromise>
          )}
          <TableSearch
            term={q}
            onChange={setQ}
            autoFocus
            placeholder={t('users.overview.search')}
          />
        </PageToolbarActions>
      </PageToolbar>

      {loading && <Loader height={500} />}
      {users && selectedUsers && (
        <>
          <FlexContainer justifyContent="flex-end">
            <Typography variant="body1" color="textSecondary">
              {users.length === 1
                ? t(`Showing ${selectedUsers.length} of ${users.length} user`)
                : t(`Showing ${selectedUsers.length} of ${users.length} users`)}
            </Typography>
          </FlexContainer>

          <UserTable
            ds={selectedUsers}
            columns={selectedColumns}
            sorters={sorters}
            variant="contained"
            rowToHref={(d) => ROUTES.users.profile.url(d.id)}
            headProps={{ sticky: true, offset: 124 }}
            dateFormat={org.dateFormats.MMDDYYYY}
            sortKey={sortKey}
            sortDirection={sortDirection}
            onSortChange={
              (k, dir) => {
                  setSortKey(k);
                  setSortDirection(dir);
                }
            }
            onColumnReorder={setSelectedColumns}
            horizontalScroll
          />
        </>
      )}

      {filterDialog.dialogOpen && (
        <UserFilterDialog
          open={true}
          onClose={filterDialog.closeDialog}
          value={filters}
          onChange={setFilters}
          orgId={org.id}
          customFields={org.customUserFields}
          title="User Filters"
        />
      )}

      {saveViewModal.dialogOpen && (
        <SaveViewModal
          open={true}
          onClose={saveViewModal.closeDialog}
          onSave={handleSaveView}
        />
      )}
    </PageBody>
  );
};

export const PageOrgUsersOverview = () => {
  const currentUser = useCurrentUser();
  const { ROUTES } = useRoutes();

  if (!hasPermissionToAccessPage(currentUser, ROUTES.users.overview)) {
    return <NoAccess />;
  }

  if (isFreeTierOrg(currentUser.org)) {
    return <Redirect to={ROUTES.users.standalone.url()} />;
  }

  return <ContentOrgUsersOverview />;
};
